

import React, { useState, useEffect, useMemo } from 'react';
import { Schedule, HierarchyItem, ResourceEntity, SystemEntity, ResourcePlanEntity } from '../../../types';
import { useRepositories } from '../../../contexts/RepositoryContext';
import { X, FileText, Network, Box, Settings } from 'lucide-react';
import GeneralTab from './GeneralTab';
import LogicTab from './LogicTab';
import ResourcesTab from './ResourcesTab';
import ConfigTab from './ConfigTab';
import { TabType, WinningResourceInfo } from './types';

interface DetailsPanelProps {
  node: Schedule | null;
  allNodes: Schedule[];
  onClose: () => void;
  onUpdate: (id: string, field: string, value: unknown) => void;
  onAddResourceRequest: () => void;
}

const TabButton = ({ id, label, icon, activeTab, onSelect }: { id: TabType, label: string, icon: React.ReactNode, activeTab: TabType, onSelect: (id: TabType) => void }) => (
    <button
      onClick={() => onSelect(id)}
      className={`flex items-center gap-2 py-3 px-1 border-b-2 transition-colors text-sm font-medium ${
          activeTab === id
          ? 'border-teal-600 text-teal-700'
          : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
      }`}
    >
      {icon}
      {label}
    </button>
);

const DetailsPanel: React.FC<DetailsPanelProps> = ({ node, allNodes, onClose, onUpdate, onAddResourceRequest }) => {
  const repositories = useRepositories();
  const [activeTab, setActiveTab] = useState<TabType>('general');

  // Async State
  const [availableLocations, setAvailableLocations] = useState<HierarchyItem[]>([]);
  const [availableApprovalGroups, setAvailableApprovalGroups] = useState<HierarchyItem[]>([]);

  const [resolvedData, setResolvedData] = useState<{
    resourceMap: Map<string, ResourceEntity>;
    systemMap: Map<string, SystemEntity>;
    planMap: Map<string, ResourcePlanEntity>;
    loading: boolean;
  }>({
    resourceMap: new Map(),
    systemMap: new Map(),
    planMap: new Map(),
    loading: false,
  });

  // Init Dropdowns
  useEffect(() => {
      const init = async () => {
          try {
             const [locs, apps] = await Promise.all([
                 repositories.getLocations({ pageSize: 100 }),
                 repositories.getApprovalGroups({ pageSize: 100 })
             ]);
             setAvailableLocations(locs.items);
             setAvailableApprovalGroups(apps.items);
          } catch (e) {
              console.error("Failed to load options", e);
          }
      };
      init();
  }, [repositories]);

  // Load Node Data
  useEffect(() => {
    if (node) {
      const resolveData = async () => {
        setResolvedData(prev => ({ ...prev, loading: true }));
        const resIds = new Set<string>();
        const sysIds = new Set<string>();
        const planIds = new Set<string>();

        [...node.resources, ...(node._inheritedResources || [])].forEach(r => {
            resIds.add(r.resourceId);
            if (r.systemId) sysIds.add(r.systemId);
            if (r.resourcePlanId) planIds.add(r.resourcePlanId);
        });

        if (resIds.size > 0 || sysIds.size > 0 || planIds.size > 0) {
            const [res, sys, plan] = await Promise.all([
                repositories.resolveResources(Array.from(resIds)),
                repositories.resolveSystems(Array.from(sysIds)),
                repositories.resolvePlans(Array.from(planIds))
            ]);
            setResolvedData({ resourceMap: res, systemMap: sys, planMap: plan, loading: false });
        } else {
            setResolvedData(prev => ({ ...prev, loading: false }));
        }
      };
      
      resolveData();
    }
  }, [node?.scheduleId, node?.resources, node?._inheritedResources, repositories]);

  // --- Priority & Winning Logic Calculation ---
  const resourceStatusMap = useMemo(() => {
    if (!node) return new Map<string, WinningResourceInfo>();
    
    const map = new Map<string, WinningResourceInfo>();

    // 1. Evaluate Direct Resources first
    node.resources.forEach(r => {
        map.set(r.resourceId, {
            priority: node.priority,
            sourceName: 'This Schedule',
            isDirect: true
        });
    });

    // 2. Evaluate Inherited Resources
    (node._inheritedResources || []).forEach(r => {
        const currentWinner = map.get(r.resourceId);
        // If no winner yet, or this inherited resource has STRICTLY HIGHER priority
        if (!currentWinner || r.sourceNodePriority > currentWinner.priority) {
            map.set(r.resourceId, {
                priority: r.sourceNodePriority,
                sourceName: r.sourceNodeName,
                isDirect: false
            });
        }
    });

    return map;
  }, [node?.resources, node?._inheritedResources, node?.priority]);


  if (!node) return null;

  const handleUpdate = (field: string, value: unknown) => {
    onUpdate(node.scheduleId, field, value);
  };

  const getScheduleName = (id: string | null) => {
      if (!id) return '';
      const s = allNodes.find(n => n.scheduleId === id);
      return s ? s.name : id;
  };

  return (
    <>
        <div className="absolute top-0 right-0 h-full w-[500px] bg-white shadow-2xl border-l border-slate-200 transform transition-transform duration-300 ease-in-out z-50 flex flex-col">
            {/* Header */}
            <div className="p-6 pb-0 flex-none">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900">Edit Schedule</h2>
                        <div className="flex items-center gap-2 text-xs text-slate-400 font-mono mt-1">
                            <span>ID:</span>
                            <span className="truncate max-w-[200px]">{node.scheduleId}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 hover:text-teal-600 transition-colors">
                        <X size={20} />
                    </button>
                </div>
                
                {/* Tabs */}
                <div className="flex gap-6 border-b border-slate-200">
                    <TabButton id="general" label="General" icon={<FileText size={14} />} activeTab={activeTab} onSelect={setActiveTab} />
                    <TabButton id="logic" label="Logic" icon={<Network size={14} />} activeTab={activeTab} onSelect={setActiveTab} />
                    <TabButton id="resources" label="Resources" icon={<Box size={14} />} activeTab={activeTab} onSelect={setActiveTab} />
                    <TabButton id="config" label="Config" icon={<Settings size={14} />} activeTab={activeTab} onSelect={setActiveTab} />
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
                {activeTab === 'general' && (
                    <GeneralTab 
                        node={node} 
                        handleUpdate={handleUpdate} 
                        allNodes={allNodes}
                        getScheduleName={getScheduleName}
                    />
                )}
                {activeTab === 'logic' && (
                    <LogicTab 
                        node={node}
                        handleUpdate={handleUpdate}
                        availableLocations={availableLocations}
                        availableApprovalGroups={availableApprovalGroups}
                    />
                )}
                {activeTab === 'resources' && (
                    <ResourcesTab 
                        node={node}
                        handleUpdate={handleUpdate}
                        inheritedResources={node._inheritedResources || []}
                        resourceStatusMap={resourceStatusMap}
                        resourceMap={resolvedData.resourceMap}
                        systemMap={resolvedData.systemMap}
                        planMap={resolvedData.planMap}
                        loading={resolvedData.loading}
                        onAddResourceRequest={onAddResourceRequest}
                    />
                )}
                {activeTab === 'config' && (
                    <ConfigTab 
                        node={node}
                        handleUpdate={handleUpdate}
                    />
                )}
            </div>
        </div>
    </>
  );
};

export default DetailsPanel;